import { useState, useEffect, useContext, useRef } from "react";
import moment from "moment";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ChipType } from "../../types/componentTypes";
import { LoansContext } from "../../context/LoansContext";
import { Divider } from "@mui/material";

export default function CustomChip(props: ChipType) {
  const { size, label, variant, className, showInfo } = props;
  const loanContextData = useContext(LoansContext);
  const { loanDetails } = loanContextData;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [infoDetails, setInfoDetails] = useState<{
    status: string;
    actionBy: string;
    date: string;
    reason?: string;
  } | null>(null);

  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (showInfo && loanDetails) {
      const latestEscalation =
        loanDetails?.escalations?.[loanDetails?.escalations?.length - 1] ?? {};
      // Approve/Reject appends a resolution entry rather than editing the
      // original one - it carries updatedBy/updatedOn and an
      // approvalComment/rejectionComment instead of the escalation's own
      // createdBy/createdOn/escalationComment.
      const isResolved = latestEscalation?.underwritingStatus !== "Pending";
      const commentType =
        latestEscalation?.underwritingStatus === "Approved"
          ? "approvalComment"
          : "rejectionComment";
      setInfoDetails({
        status: latestEscalation?.underwritingStatus || "",
        actionBy: isResolved
          ? latestEscalation?.updatedBy
          : latestEscalation?.createdBy,
        date: isResolved
          ? latestEscalation?.updatedOn
            ? moment(latestEscalation.updatedOn).format("MM/DD/YYYY")
            : ""
          : latestEscalation?.createdOn
            ? moment(latestEscalation.createdOn).format("MM/DD/YYYY")
            : "",
        reason:
          (isResolved
            ? latestEscalation?.[commentType]
            : latestEscalation?.escalationComment) || "",
      });
    }
  }, [showInfo, loanDetails]);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setAnchorEl(null), 150);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (showInfo) {
      cancelClose();
      setAnchorEl(e.currentTarget);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      display="inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={scheduleClose}
    >
      <Chip
        size={size}
        label={
          showInfo ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {label}
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </Box>
          ) : (
            label
          )
        }
        variant={variant}
        className={className}
      />

      {showInfo && infoDetails && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setAnchorEl(null)}
          disableRestoreFocus
          slotProps={{
            paper: {
              onMouseEnter: cancelClose,
              onMouseLeave: scheduleClose,
              sx: {
                mt: 0,
                borderRadius: "8px",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
                border: "0.5px solid",
                borderColor: "divider",
              },
            },
          }}
        >
          <Box sx={{ p: 1.5, maxWidth: 280 }}>
            <Typography
              variant="caption"
              display="block"
              fontWeight={600}
              sx={{ mb: 1, color: "text.primary", fontSize: "0.75rem" }}
            >
              Review Details
            </Typography>
            <Divider />
            {[
              {
                label:
                  infoDetails.status === "Approved"
                    ? "Approved by"
                    : infoDetails.status === "Rejected"
                      ? "Rejected by"
                      : "Escalated by",
                value: infoDetails.actionBy,
              },
              { label: "Date", value: infoDetails.date },
              { label: "Reason", value: infoDetails.reason },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1.5,
                  mb: 0.5,
                  mt: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {label} :
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={500}
                  sx={{ textAlign: "right", wordBreak: "break-word" }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Popover>
      )}
    </Box>
  );
}
